import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

// Shared browse-page filter plumbing (browse_filters_and_mobile_panel.md). Reads/writes every
// filter as a URL query param (shareable, bookmarkable, back-button friendly) and hands the page:
//
//  get(key)      current value of a filter param ('' when unset)
//  setParam / setSort / setPrice   write one/some params (functional update, so a min+max price
//                write in the same tick doesn't clobber itself)
//  apiParams     the object to pass straight to the list API (only set params, page 1)
//  sig           a string that changes iff apiParams changes - a stable useEffect dep
//  activeCount   how many filters are applied (price range counts as one)
//  clearAll      drop every filter param (keeps q / sort... actually keeps nothing filter-y)
//
// `filterKeys` is this page's structured-filter param names, e.g.
// ['category','transmission','fuel','seats']. Price (minPrice/maxPrice) and sort are always handled.
export default function useBrowseFilters(filterKeys) {
  const [searchParams, setSearchParams] = useSearchParams();

  const get = useCallback((k) => searchParams.get(k) || '', [searchParams]);

  const writeMany = useCallback((patch) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(patch).forEach(([k, v]) => {
        if (v === '' || v == null) next.delete(k);
        else next.set(k, String(v));
      });
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const setParam = useCallback((k, v) => writeMany({ [k]: v }), [writeMany]);
  const setSort = useCallback(
    (v) => writeMany({ sort: v && v !== 'recommended' ? v : '' }),
    [writeMany]
  );
  const setPrice = useCallback(({ min, max }) => writeMany({
    minPrice: min == null ? '' : Math.round(min),
    maxPrice: max == null ? '' : Math.round(max)
  }), [writeMany]);

  const priceKeys = ['minPrice', 'maxPrice'];
  const allKeys = [...new Set([...filterKeys, 'location', ...priceKeys])];

  const q = get('q');
  const sort = get('sort') || 'recommended';
  const minPrice = get('minPrice');
  const maxPrice = get('maxPrice');

  const sig = [q, sort, ...allKeys.map((k) => get(k))].join('|');

  const activeCount = [...new Set([...filterKeys, 'location'])].filter((k) => get(k)).length + (minPrice || maxPrice ? 1 : 0);

  const apiParams = useMemo(() => {
    const p = { sort, page: 1 };
    if (q) p.q = q;
    allKeys.forEach((k) => {
      const v = get(k);
      if (v) p[k] = v;
    });
    return p;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);

  const clearAll = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      allKeys.forEach((k) => next.delete(k));
      return next;
    }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setSearchParams]);

  // The current browse URL (path + query), so an item card can hand it to the detail page as
  // router state and "← Back to listings" returns the customer to exactly the filtered/sorted
  // view they left (homepage/user_experience.md #3). '/' when nothing is applied.
  const qs = searchParams.toString();
  const browsePath = qs ? `/?${qs}` : '/';

  return {
    get, setParam, setSort, setPrice, clearAll,
    q, sort, minPrice, maxPrice, sig, activeCount, apiParams, browsePath,
    searchParams, setSearchParams
  };
}
